import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import { Container, Box, Typography, IconButton } from '@material-ui/core';
import DeleteForever from '@material-ui/icons/CancelRounded';
import uploadFilesIcon from '../icons/color/ic_UploadFiles.svg';
import iconPdf from '../icons/color/icr_pdf_file.svg';
import { useUploadFilesStyles } from './styles';

const UploadFiles = ({ ...props }) => {
  const { mobile, onFileUpload, issueFormName, acceptPdf } = props;
  const [files, setFiles] = useState([]);
  const classes = useUploadFilesStyles({ mobile });

  const onDrop = useCallback(
    (acceptedFiles, rejections) => {
      if (rejections?.length > 0 || acceptedFiles.length === 0) {
        files?.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
        setFiles([]);
        onFileUpload([], rejections);
        return;
      }

      const nextFiles = acceptedFiles.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) }),
      );
      setFiles(nextFiles);
      onFileUpload(acceptedFiles, []);
    },
    [onFileUpload, files],
  );

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    onDropRejected: (rej) => {
      files?.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
      setFiles([]);
      onFileUpload([], rej);
    },
    maxFiles: 3,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png'],
      ...(acceptPdf && { 'application/pdf': ['.pdf'] }),
    },
  });

  const removeFile = (name) => {
    setFiles((files) => files.filter((file) => file.name !== name));
  };

  useEffect(() => {
    return () =>
      files?.forEach(
        (file) => file.preview && URL.revokeObjectURL(file.preview),
      );
  }, [files]);

  return (
    <Container>
      <Box
        data-cy={`uploadFiles${issueFormName}`}
        className={classes.container}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <img
          className={classes.icon}
          src={uploadFilesIcon}
          alt="upload-files-icon"
        />
        <div>
          {mobile ? (
            <Typography className={classes.title}>
              Sube tus archivos haciendo clic aquí.
            </Typography>
          ) : (
            <Typography className={classes.title}>
              Arrastra o sube tus archivos haciendo clic aquí.
            </Typography>
          )}
          <Typography className={classes.subtitle}>
            Máximo 3 {acceptPdf ? 'archivos' : 'imágenes'}
          </Typography>
        </div>
      </Box>

      <Box className={classes.thumbsContainer}>
        {files?.map((file, index) => {
          const isPdf = file.type === 'application/pdf';
          return (
            <div className={classes.thumb} key={file.name}>
              <div className={classes.thumbInner}>
                {isPdf ? (
                  <img
                    src={iconPdf}
                    alt={file.name}
                    className={classes.image}
                  />
                ) : (
                  <img
                    src={file.preview}
                    alt={file.name}
                    className={classes.image}
                    onLoad={() => URL.revokeObjectURL(file.preview)}
                    data-cy={`file${file.name}`}
                  />
                )}
              </div>
              <IconButton
                className={classes.iconImage}
                onClick={() => removeFile(file.name, index)}
              >
                <DeleteForever />
              </IconButton>
            </div>
          );
        })}
      </Box>

      {fileRejections.length > 0 && (
        <Typography className={classes.rejectionsMessage}>
          Se permite un máximo de 3{' '}
          {acceptPdf
            ? 'archivos o imágenes en formato [.jpg, .jpeg, .png, .pdf]'
            : 'imágenes en formato [.jpg, .jpeg, .png]'}
        </Typography>
      )}
    </Container>
  );
};

UploadFiles.propTypes = {
  mobile: PropTypes.bool,
  onFileUpload: PropTypes.func,
  issueFormName: PropTypes.string,
  acceptPdf: PropTypes.bool,
};

export default UploadFiles;
